import { Injectable } from "@angular/core";
import { MenuController } from "@ionic/angular";
import { Form, GeneratedWorkout } from "@me-fit-mono/typings";
import { BehaviorSubject, firstValueFrom, map, Subject } from "rxjs";
import { BreakpointsService } from "./breakpoints.service";
import { FeatureFlipService } from "./feature-flip.service";

export interface CopilotPaneFields {
  promptPlaceholder?: string;
  buttonLabel?: string;
}

export interface CopilotContextButton {
  label: string;
  callback: () => Promise<any>;
}

export interface CopilotPaneOutput {
  type: "workout" | "form";
  value: unknown;
}

export interface OpenCopilotPaneWorkoutContext {
  type: "workout";
  customerId?: string;
  name?: string;
  description?: string;
  steps: GeneratedWorkout['steps'];
}

export interface OpenCopilotPaneFormContext {
  type: "form";
  /** The current form being edited, if any. Passed to the AI so it can modify rather than regenerate. */
  currentForm?: Pick<Form, 'formType' | 'title' | 'description' | 'fields'>;
}

export interface OpenCopilotPaneOptions {
  fields?: CopilotPaneFields;
  context: OpenCopilotPaneWorkoutContext | OpenCopilotPaneFormContext;
}


type CopilotPaneStateClosed = { open: false };

export type CopilotPaneStateOpen = {
  open: true;
  /**
   * Incase the user wants to hide the pane, but open it again later.
   */
  collapsed: boolean;
} & OpenCopilotPaneOptions;

export type CopilotPaneState = CopilotPaneStateClosed | CopilotPaneStateOpen;

type StoredSystemMessage = Record<OpenCopilotPaneOptions['context']['type'], string>;

/**
 * This service will facilitate the interaction with the Copilot pane in the application.
 */
@Injectable({
  providedIn: 'root'
})
export class CopilotPaneService {
  state$ = new BehaviorSubject<CopilotPaneState>({
    open: false
  });

  isOpen$ = this.state$.pipe(
    map(state => state.open)
  );

  fields$ = this.state$.pipe(
    map(state => state.open ? state.fields : {})
  );

  contextButtons$ = new BehaviorSubject<CopilotContextButton[]>([]);
  output$ = new Subject<CopilotPaneOutput>();


  private defaultSystemMessage: StoredSystemMessage = {
    workout: '',
    form: `You are an expert assistant for creating fitness assessments or tests (forms) for organizations. Given a user prompt, generate a JSON object that matches the required schema for an assessment or test.
Respond ONLY with a valid JSON object that matches the schema below. Do not include any extra text.
If you do not have enough information, omit the 'form' key and ask for clarification as part of the copilotMessage.`
  };

  private readonly systemMessageLocalStorageKey = 'copilot-system-prompt';

  private _systemMessageValue: StoredSystemMessage | null = null;

  constructor(
    private menuCtrl: MenuController,
    private $featureFlip: FeatureFlipService,
    private breakpointsService: BreakpointsService
  ) {

  }

  get systemMessage(): StoredSystemMessage {
    if (this._systemMessageValue === null) {
      const storedMessage = localStorage.getItem(this.systemMessageLocalStorageKey);

      if (storedMessage) {
        try {
          this._systemMessageValue = JSON.parse(storedMessage);
        } catch (e) {
          console.error('Failed to parse stored system message', e);
          this._systemMessageValue = this.defaultSystemMessage;
        }
      } else {
        this._systemMessageValue = this.defaultSystemMessage;
      }

      return this._systemMessageValue!;
    } else {
      return this._systemMessageValue;
    }

  }

  set systemMessage(message: StoredSystemMessage | null) {
    localStorage.setItem(this.systemMessageLocalStorageKey, JSON.stringify(message));
    this._systemMessageValue = message;
  }

  async open({fields, context}: OpenCopilotPaneOptions) {

    const isEnabled = await firstValueFrom(this.$featureFlip.copilotPaneEnabled$)

    if (!isEnabled) {
      return;
    }

    // Open the Copilot pane by toggling the side menu
    this.menuCtrl.open('copilotMenu');
    this.state$.next({
      open: true,
      fields,
      context,
      // This ensures we dont open the copilot modal on mobile, and user has to explicitly open it, as it takes up a lot of screen space and can be annoying to have open by default.
      collapsed: !this.breakpointsService.isAboveLargeScreen()
    });
  }

  setCollapsed(collapsed: boolean) {
    const currentState = this.state$.value;

    if (!currentState.open) {
      return;
    }

    this.state$.next({
      ...currentState,
      collapsed
    });
  }

  close() {
    this.menuCtrl.close('copilotMenu');
    this.state$.next({
      open: false
    });
  }

  setContextButtons(buttons: CopilotContextButton[]) {
    this.contextButtons$.next(buttons);
  }

  updateContext(newContext: Partial<OpenCopilotPaneOptions['context']>) {

    const currentState = this.state$.value;

    if (!currentState.open) {
      return;
    }

    this.state$.next({
      ...currentState,
      context: {
        ...currentState.context,
        // The compiler is overly strict that this is a Partial, when are spreading the existing context first, so we need to assert it as the full type
        ...newContext as OpenCopilotPaneOptions['context']
      }
    });
  }

  emitOutput(output: CopilotPaneOutput) {
    this.output$.next(output);
  }

  resetSystemMessageToDefault() {
    this.systemMessage = this.defaultSystemMessage;
  }
}
